/* Partner Insurance Portal pages (Phase PP-3 · Feb 2026).
   4 page exports. Theme: emerald.
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Shield, FileText, Activity, RefreshCw, Coins, FileWarning,
  ShieldCheck, AlertTriangle, ScrollText, Check, X, Send,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

function KpiTile({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const fmtMoney = n => n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtTime = s => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };

const COVER_TONE = { accepted: "emerald", pending: "amber", refused: "rose", revoked: "slate" };
const CLAIM_TONE = { paid: "emerald", approved: "sky", filed: "amber", open: "amber", in_review: "amber", denied: "rose" };

// ════════════════════════════════════════════════════════════════════════
// OVERVIEW
// ════════════════════════════════════════════════════════════════════════
export function InsuranceOverview() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const load = async () => {
    const { data } = await api.get("/partner/insurance/overview", { params: { since_days: days } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  return (
    <div data-testid="insurance-overview" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Shield className="w-6 h-6 text-emerald-500"/> Insurance Overview
          </h1>
          <p className="text-sm text-slate-600 mt-1">Coverage + claims pipeline + underwriting applications.</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="insurance-window"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="insurance-refresh"><RefreshCw className="w-4 h-4 mr-1.5"/> Refresh</Button>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="insurance-kpis">
            <KpiTile testid="ins-kpi-covers"     label="Covers active"   value={data.covers.active}        sub={`${data.covers.total} total`} icon={ShieldCheck} tone="emerald"/>
            <KpiTile testid="ins-kpi-limits"     label="Coverage limit"  value={fmtMoney(data.covers.limit_sum_usd)} icon={Coins} tone="sky"/>
            <KpiTile testid="ins-kpi-apps"       label="Apps active"     value={data.applications.active}  sub={`${data.applications.total} total`} icon={ScrollText} tone="violet"/>
            <KpiTile testid="ins-kpi-claims-open" label="Open claims"    value={data.claims.open}          sub={`${data.claims.total} lifetime`} icon={FileWarning} tone={data.claims.open ? "amber" : "slate"}/>
            <KpiTile testid="ins-kpi-loss"       label="Loss ratio"      value={`${data.claims.loss_ratio_pct}%`} sub={`${fmtMoney(data.claims.paid_sum_usd)} paid`} icon={AlertTriangle} tone={data.claims.loss_ratio_pct > 40 ? "rose" : "slate"}/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiTile testid="ins-revoked" label="Revoked covers" value={data.covers.revoked} icon={X} tone="slate" />
            <KpiTile testid="ins-paid"    label="Paid claims"    value={data.claims.paid}     icon={Check} tone="emerald" />
            <KpiTile testid="ins-paid-sum" label="Total paid out" value={fmtMoney(data.claims.paid_sum_usd)} icon={Coins} tone="violet" />
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// COVERS
// ════════════════════════════════════════════════════════════════════════
export function InsuranceCovers() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    const { data } = await api.get("/partner/insurance/covers", { params });
    setRows(data.rows); setByStatus(data.by_status || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="insurance-covers" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><Shield className="w-5 h-5"/> Covers</h1>
      <div className="grid grid-cols-4 gap-3" data-testid="covers-strip">
        {["accepted", "pending", "refused", "revoked"].map(s => (
          <KpiTile key={s} testid={`covers-strip-${s}`} label={s} value={byStatus[s] || 0} tone={COVER_TONE[s]} />
        ))}
      </div>
      <div className="flex gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="covers-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["accepted","pending","refused","revoked"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Seller</div>
          <div className="col-span-2">Limit</div>
          <div className="col-span-2">Coverage</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        <div data-testid="covers-table">
          {rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="covers-empty">No covers.</div>}
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm items-center" data-testid={`cover-row-${r.id}`}>
              <div className="col-span-3 truncate">{r.buyer_name}</div>
              <div className="col-span-2 truncate text-xs text-slate-600">{r.seller_name}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.permanent_limit || r.requested_limit)}</div>
              <div className="col-span-2 text-xs">{r.coverage_pct ? `${(r.coverage_pct*100).toFixed(0)}%` : "—"} @ {r.premium_rate ? `${(r.premium_rate*100).toFixed(2)}%` : "—"}</div>
              <div className="col-span-2"><span className={`text-[10px] px-2 py-0.5 rounded-full bg-${COVER_TONE[r.status] || "slate"}-50 text-${COVER_TONE[r.status] || "slate"}-700 border border-${COVER_TONE[r.status] || "slate"}-200`}>{r.status}</span></div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`cover-manage-${r.id}`}><FileText className="w-3.5 h-3.5"/></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <CoverDialog cover={open} onClose={() => setOpen(null)} onSaved={load} />
    </div>
  );
}

function CoverDialog({ cover, onClose, onSaved }) {
  const [status, setStatus] = useState("accepted");
  const [newLimit, setNewLimit] = useState("");
  const [refusalReason, setRefusalReason] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (cover) { setStatus(cover.status || "accepted"); setNewLimit(String(cover.permanent_limit || "")); setRefusalReason(""); setReason(""); }
  }, [cover?.id]);
  if (!cover) return null;
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const body = { status, reason };
      if (newLimit) body.new_permanent_limit = Number(newLimit);
      if (refusalReason) body.refusal_reason = refusalReason;
      await api.patch(`/partner/insurance/covers/${cover.id}`, body);
      toast.success(`Cover updated → ${status}`);
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!cover} onOpenChange={onClose}>
      <DialogContent data-testid="cover-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage cover</DialogTitle>
          <DialogDescription>{cover.buyer_name} · {cover.seller_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="cover-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                {["accepted","refused","revoked"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>New permanent limit (optional)</Label><Input type="number" value={newLimit} onChange={e => setNewLimit(e.target.value)} data-testid="cover-limit"/></div>
          {status === "refused" && (
            <div><Label>Refusal reason</Label><Textarea value={refusalReason} onChange={e => setRefusalReason(e.target.value)} rows={2} data-testid="cover-refusal"/></div>
          )}
          <div><Label>Audit reason</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="cover-reason"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="cover-submit" onClick={submit} disabled={busy || reason.trim().length < 3}>{busy ? "Saving…" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
// CLAIMS
// ════════════════════════════════════════════════════════════════════════
export function InsuranceClaims() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    const { data } = await api.get("/partner/insurance/claims", { params });
    setRows(data.rows); setByStatus(data.by_status || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div data-testid="insurance-claims" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><FileWarning className="w-5 h-5"/> Claims</h1>
      <div className="grid grid-cols-5 gap-3" data-testid="claims-strip">
        {["filed", "approved", "paid", "denied", "open"].map(s => (
          <KpiTile key={s} testid={`claims-strip-${s}`} label={s} value={byStatus[s] || 0} tone={CLAIM_TONE[s] || "slate"} />
        ))}
      </div>
      <div className="flex gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="claims-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["filed","approved","paid","denied","open"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">Filed</div>
          <div className="col-span-2">Invoice</div>
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Claimed</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        <div data-testid="claims-table">
          {rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="claims-empty">No claims.</div>}
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm items-center" data-testid={`claim-row-${r.id}`}>
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.filed_at)}</div>
              <div className="col-span-2 font-mono text-xs">{r.invoice_code}</div>
              <div className="col-span-3 truncate">{r.buyer_name}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.claimed_amount)}</div>
              <div className="col-span-2"><span className={`text-[10px] px-2 py-0.5 rounded-full bg-${CLAIM_TONE[r.status] || "slate"}-50 text-${CLAIM_TONE[r.status] || "slate"}-700 border border-${CLAIM_TONE[r.status] || "slate"}-200`}>{r.status}</span></div>
              <div className="col-span-1 text-right">
                {!["paid","denied"].includes(r.status) && (
                  <Button size="sm" variant="outline" onClick={() => setOpen(r)} data-testid={`claim-decide-${r.id}`}><Send className="w-3.5 h-3.5"/></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <ClaimDecisionDialog claim={open} onClose={() => setOpen(null)} onSaved={load} />
    </div>
  );
}

function ClaimDecisionDialog({ claim, onClose, onSaved }) {
  const [decision, setDecision] = useState("approve");
  const [covered, setCovered] = useState("");
  const [denyReason, setDenyReason] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (claim) { setDecision("approve"); setCovered(String(claim.covered_amount || claim.claimed_amount || "")); setDenyReason(""); setReason(""); }
  }, [claim?.id]);
  if (!claim) return null;
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const body = { decision, reason };
      if (covered) body.covered_amount = Number(covered);
      if (denyReason) body.deny_reason = denyReason;
      await api.patch(`/partner/insurance/claims/${claim.id}/decision`, body);
      toast.success(`Claim ${decision}d`);
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Decision failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!claim} onOpenChange={onClose}>
      <DialogContent data-testid="claim-decision-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Claim decision · {claim.invoice_code}</DialogTitle>
          <DialogDescription>{claim.buyer_name} · {fmtMoney(claim.claimed_amount)} claimed</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Decision</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {["approve","deny","pay"].map(d => (
                <Button key={d} type="button" variant={decision === d ? "default" : "outline"} onClick={() => setDecision(d)} data-testid={`decision-${d}`}>{d}</Button>
              ))}
            </div>
          </div>
          {decision !== "deny" && <div><Label>Covered amount</Label><Input type="number" value={covered} onChange={e => setCovered(e.target.value)} data-testid="claim-covered"/></div>}
          {decision === "deny" && <div><Label>Deny reason</Label><Textarea value={denyReason} onChange={e => setDenyReason(e.target.value)} rows={2} data-testid="claim-deny"/></div>}
          <div><Label>Audit reason</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="claim-reason"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="claim-submit" onClick={submit} disabled={busy || reason.trim().length < 3}>{busy ? "Saving…" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
// APPLICATIONS
// ════════════════════════════════════════════════════════════════════════
export function InsuranceApplications() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/partner/insurance/applications").then(r => setRows(r.data.rows)); }, []);
  return (
    <div data-testid="insurance-applications" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><ScrollText className="w-5 h-5"/> Underwriting applications</h1>
      <KpiTile testid="apps-total" label="Applications" value={rows.length} tone="violet" />
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Seller</div>
          <div className="col-span-3">Policy</div>
          <div className="col-span-2">Coverage</div>
          <div className="col-span-2">Premium</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Valid</div>
        </div>
        <div data-testid="apps-table">
          {rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="apps-empty">No applications.</div>}
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
              <div className="col-span-3 truncate">{r.seller_name}</div>
              <div className="col-span-3 font-mono text-xs">{r.policy_number || "—"}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.coverage_limit)}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.monthly_premium)}<span className="text-xs text-slate-400">/mo</span></div>
              <div className="col-span-1 text-xs"><Badge variant="outline">{r.status}</Badge></div>
              <div className="col-span-1 text-right text-xs text-slate-500">{fmtTime(r.valid_until)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
